import { PropsWithChildren } from 'react'
import { useTranslation } from 'summon-ui/intl'
import { Button, Flex, Title } from 'summon-ui/mantine'
import { useQueryClient } from '@tanstack/react-query'
import { graphqlClient } from '@api/config'
import { TENANT_CONFIG } from '@config'
import { useFinishTutorialMutation } from '@generated/generates'
import { TourProvider, StylesObj, Position } from '@reactour/tour'

const { features, isDarkMode } = TENANT_CONFIG

const HOME_TOUR = {
  steps: [
    {
      selector: '#action-button_Inventory',
      content:
        'This is where you can dress your avatar and change their wearables. Your avatar is soulbound and will collect items and achievements as you progress.',
      padding: { popover: 20 }
    },
    ...(features.crafting
      ? [
          {
            selector: '#action-button_Crafting',
            content:
              "As you complete quests, missions, and levels, you'll gather materials you can craft into special gear and items to equip on your avatar.",
            padding: { popover: 20 }
          }
        ]
      : []),
    {
      selector: '#nav-button_Quests',
      padding: { popover: 20 },
      content:
        "Quests are your guide to unlock the full potential of your community. Complete more quests and missions to earn the XP you'll need to reach high levels of achievement and influence.",
      position: 'right' as Position
    },
    ...(features.proposals
      ? [
          {
            selector: '#nav-button_Proposals',
            padding: { popover: 20 },
            content:
              'Proposals are how community decisions are made. As you progress in level, your influence will continue to increase. Be sure to check in frequently to not miss a thing, and ensure your voice is heard!',
            position: 'right' as Position
          }
        ]
      : []),
    {
      selector: '#none',
      padding: { popover: 20 },
      content: "There's a lot more to discover, but that should keep you busy for now. Feel free to look around.",
      position: 'center' as Position
    }
  ]
}

const styles = {
  popover: (base: StylesObj) => ({
    ...base,
    ...{
      '--reactour-accent': 'var(--mantine-color-primary-5)'
    },
    ...(isDarkMode
      ? {
          backgroundColor: 'var(--mantine-color-dark-7)',
          opacity: 0.85,
          border: '1px solid var(--mantine-color-dark-6)',
          color: 'white'
        }
      : {})
  })
}

const Navigation = ({
  currentStep,
  setCurrentStep,
  setIsOpen
}: {
  currentStep: number
  setCurrentStep: (step: number) => void
  setIsOpen: (isOpen: boolean) => void
}) => {
  const t = useTranslation()
  const isLastStep = currentStep === HOME_TOUR.steps.length - 1
  return (
    <Flex w='100%' justify='space-between' pt='lg' align='center' gap='xs'>
      <Title order={6}>
        {t('Step')} {currentStep + 1}/{HOME_TOUR.steps.length}
      </Title>
      <Flex gap='sm'>
        <Button
          variant='outline'
          onClick={() => {
            setCurrentStep(currentStep - 1)
          }}
        >
          {t('Previous')}
        </Button>
        <Button
          onClick={() => {
            isLastStep ? setIsOpen(false) : setCurrentStep(currentStep + 1)
          }}
        >
          {isLastStep ? t('Close') : t('Next')}
        </Button>
      </Flex>
    </Flex>
  )
}

const Tutorial = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient()
  const { mutate } = useFinishTutorialMutation(graphqlClient(), {
    onSuccess() {
      // Refresh user to get 'hasCompletedTutorial' updated
      queryClient.invalidateQueries({
        queryKey: ['GetUser']
      })
    }
  })

  return (
    <TourProvider
      position='bottom'
      showBadge={false}
      steps={HOME_TOUR.steps}
      className='rounded-lg !p-6'
      beforeClose={() => mutate({})}
      styles={styles}
      components={{
        Navigation,
        Close: () => {
          return null
        }
      }}
      showCloseButton
      maskClassName='some'
      showNavigation
      showDots
      highlightedMaskClassName='reacttour-highlightedMask'
      disableInteraction
    >
      {children}
    </TourProvider>
  )
}

export default Tutorial
